import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Card, CardBody, Row, Col, Table } from "reactstrap"
import { useForm } from "react-hook-form"
import { fetchFinanceAccountGroupSearch, deleteFinanceAccountGroup } from "../../services/finance/finance"
import FinanceCat from "../../components/Common/chooseFields/financeCat"
import EditFinanceGroup from "../Finance/groups/edit"
import { toastError, toastSuccess } from "../../helpers/msg_helper"

const NATURE = { 1: "Assets", 2: "Liabilities", 3: "Income", 4: "Expenses" }

const natureOf = g => g?.type?.name || NATURE[g?.type] || NATURE[g?.account_group_type] || "—"

/** Sortable columns → the value each one sorts on. Everything the table shows is already in hand
 *  (the whole chart is fetched in one call), so sorting stays client-side and covers the derived
 *  columns — Nature and Type — that the API cannot order by. */
const SORTABLE = {
  code: g => g.code || "",
  name: g => g.name || "",
  parent: g => g.parent?.name || "",
  nature: g => natureOf(g),
  type: g => (g.is_system ? "Default" : "Custom"),
}

/**
 * Administration → Application → Financial Groups.
 * The standard Tally chart (is_system) is read-only (locked); company-specific groups
 * are editable. These default groups were previously hardcoded in the finance code.
 */
const FinancialGroups = () => {
  const [rows, setRows] = useState([])
  const [nature, setNature] = useState("")
  const [refresh, setRefresh] = useState(0)
  const [editGroup, setEditGroup] = useState({ modal: false, value: "" })
  const [sort, setSort] = useState({ field: "code", order: "asc" })
  const { control } = useForm()
  // Developers may edit the locked default (is_system) groups; regular admins cannot.
  const isDeveloper = useSelector(s => s.Login?.loggedUser?.company_context?.is_developer)

  useEffect(() => {
    ;(async () => {
      try {
        const r = await fetchFinanceAccountGroupSearch({
          params: `&searchText${nature ? `&type=${nature}` : ""}&sortOrder=asc&sizePerPage=200`,
        })
        setRows(r?.data?.data || [])
      } catch (e) { toastError(e?.message) }
    })()
  }, [nature, refresh])

  const onUpdate = () => setRefresh(n => n + 1)

  const toggleSort = field =>
    setSort(s => ({ field, order: s.field === field && s.order === "asc" ? "desc" : "asc" }))

  const sorted = useMemo(() => {
    const pick = SORTABLE[sort.field]
    if (!pick) return rows
    const dir = sort.order === "asc" ? 1 : -1
    // codes carry a running number (AC202500001…) — numeric collation keeps 9 before 10
    return [...rows].sort((a, b) => dir * String(pick(a)).localeCompare(String(pick(b)), undefined, { numeric: true, sensitivity: "base" }))
  }, [rows, sort])

  const SortTh = ({ field, children, className = "" }) => (
    <th className={`${className} user-select-none`} role="button" onClick={() => toggleSort(field)}>
      {children}
      <i
        className={`ms-1 ${sort.field === field ? (sort.order === "asc" ? "uil-angle-up" : "uil-angle-down") : "uil-angle-down"}`}
        style={{ opacity: sort.field === field ? 1 : 0.3 }}
      />
    </th>
  )

  // the API is the authority — it refuses while ledgers, entries or sub-groups still reference the
  // group; can_delete just keeps the icon off rows that would be rejected
  const canDelete = g => g.can_delete && (!g.is_system || isDeveloper)
  const remove = async g => {
    if (!window.confirm(`Delete financial group “${g.name}”? This cannot be undone.`)) return
    try {
      await deleteFinanceAccountGroup(g.id)
      toastSuccess("Financial group deleted")
      onUpdate()
    } catch (e) { toastError(e?.message) }
  }

  return (
    <Card><CardBody>
      {/* Filter + add-button on a single compact row (keeps the table high in the tab — the
          standard-groups explainer was dropped here to reclaim that vertical space). */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div style={{ width: 280 }}>
          <FinanceCat
            control={control}
            name="nature"
            placeholder="Select Nature of Group"
            target="body"
            isClearable
            onSelectChange={e => setNature(e?.value ?? "")}
          />
        </div>
        <EditFinanceGroup
          key="add"
          onUpdate={onUpdate}
          onReset={() => setEditGroup("")}
          buttonLabel="+&nbsp; Group"
          className="px-2 me-2 btn btn-primary"
          editItem={{ modal: false, value: "" }}
        />
      </div>
      <Table responsive borderless className="align-middle tnt-table">
        <thead><tr>
          <SortTh field="code">Code</SortTh>
          <SortTh field="name">Name</SortTh>
          <SortTh field="parent">Under (parent)</SortTh>
          <SortTh field="nature">Nature</SortTh>
          <SortTh field="type">Type</SortTh>
          <th className="text-end">Actions</th>
        </tr></thead>
        <tbody>
          {sorted.map(g => (
            <tr key={g.id}>
              <td>{g.code || "—"}</td>
              <td>{g.name}</td>
              <td>{g.parent?.name || "—"}</td>
              <td>{natureOf(g)}</td>
              <td>{g.is_system ? <span className="badge bg-light text-muted">Default</span> : <span className="badge bg-info-subtle text-info">Custom</span>}</td>
              <td className="text-end text-nowrap">
                {g.is_system && !isDeveloper ? (
                  <i className="uil-lock action-icon" title="Default group — locked" style={{ opacity: 0.5 }} />
                ) : (
                  <i
                    className="uil-pen action-icon"
                    role="button"
                    title={g.is_system ? "Edit default group (developer)" : "Edit"}
                    onClick={() => setEditGroup({ modal: true, value: g })}
                  />
                )}
                {canDelete(g) ? (
                  <i className="uil-trash-alt action-icon danger" role="button" title="Delete" onClick={() => remove(g)} />
                ) : (
                  <i
                    className="uil-trash-alt action-icon"
                    title={g.delete_blocked_reason || "Default group — locked"}
                    style={{ opacity: 0.3, cursor: "not-allowed" }}
                  />
                )}
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan="6" className="text-center text-muted">No groups.</td></tr>}
        </tbody>
      </Table>

      {/* Hidden edit-trigger modal (opens when a custom group's edit is clicked) */}
      <EditFinanceGroup
        key={editGroup?.value?.id || "edit"}
        onUpdate={onUpdate}
        onReset={() => setEditGroup("")}
        buttonLabel=""
        className="d-none"
        editItem={editGroup}
      />
    </CardBody></Card>
  )
}

export default FinancialGroups
